import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Recaptcha from 'react-google-recaptcha';
import { useMutation } from '@apollo/client';
import Config from 'config';
import { useFormik } from 'formik';
import UPLOAD_BULK_IMPORT_FILE_MUTATION from 'graph/bulk-import-upload/upload-bulk-import-file-mutation';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  makeStyles,
  styled,
  TextField as BaseTextField,
  TextFieldProps,
  Typography
} from '@material-ui/core';
import { CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import * as Yup from 'yup';

type Props = {
  onSuccess: (onCloseCallback?: () => void) => void;
  onError: () => void;
};

const ACCEPTED_FILE_EXTS = ['.xlsx', '.xls', '.csv', '.zip'].join(',');

const FORM_SCHEMA = Yup.object().shape({
  organizationName: Yup.string().required(),
  contactName: Yup.string().required(),
  contactEmail: Yup.string()
    .email()
    .required()
});

const DEFAULT_FORM_VALUES = {
  organizationName: '',
  contactName: '',
  contactEmail: ''
};

const BulkImportUploadForm = ({ onError, onSuccess }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('BulkImportUploadForm');

  const [file, setFile] = useState<File | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const fileInputRef = React.createRef<HTMLInputElement>();
  const recaptchaRef = React.createRef<Recaptcha>();

  const [uploadFile] = useMutation(UPLOAD_BULK_IMPORT_FILE_MUTATION);

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    isValid,
    isSubmitting,
    resetForm
  } = useFormik({
    initialValues: DEFAULT_FORM_VALUES,
    validationSchema: FORM_SCHEMA,
    validateOnBlur: true,
    onSubmit: async formValues => {
      if (!file) {
        setFileError(t('invalidFile'));
      } else {
        setFileError(null);
      }

      if (!captchaToken) {
        setCaptchaError(t('invalidCaptcha'));
      } else {
        setCaptchaError(null);
      }

      if (!isValid || !file || !captchaToken) return;

      try {
        const { data, errors } = await uploadFile({
          variables: {
            data: { ...formValues, filename: file.name, size: file.size, token: captchaToken }
          }
        });

        if (errors || !data?.uploadBulkImportFile) {
          onError();
          return;
        }

        if (data?.uploadBulkImportFile) {
          const { url, fields } = data.uploadBulkImportFile;
          await handleFileUpload(url, fields);
          onSuccess();
        }
      } catch (error) {
        onError();
        console.error(error);
      }
    }
  });

  const handleCaptchaChange = (value: string | null) => setCaptchaToken(value);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleFileUpload = async (url: string, fields: Record<string, string>) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    formData.append('file', file as Blob);
    return fetch(url, { method: 'POST', body: formData });
  };

  const handleReset = () => {
    setFile(null);
    setFileError(null);
    setCaptchaError(null);
    resetForm();

    if (fileInputRef.current) fileInputRef.current.value = '';
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  return (
    <form
      id="upload_form"
      name="upload_form"
      onSubmit={handleSubmit}
      onReset={handleReset}
      className={classes.form}
    >
      <TextField
        required
        label={t('organizationName')}
        name="organizationName"
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.organizationName}
        error={touched.organizationName && !!errors.organizationName}
        helperText={
          touched.organizationName &&
          !!errors.organizationName &&
          t('requiredHelperText', { ns: '@common' })
        }
      />
      <TextField
        required
        label={t('contactName')}
        name="contactName"
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.contactName}
        error={touched.contactName && !!errors.contactName}
        helperText={
          touched.contactName && !!errors.contactName && t('requiredHelperText', { ns: '@common' })
        }
      />
      <TextField
        required
        label={t('contactEmail')}
        name="contactEmail"
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.contactEmail}
        error={touched.contactEmail && !!errors.contactEmail}
        helperText={touched.contactEmail && !!errors.contactEmail && t('invalidEmail')}
      />
      <Box className={classes.fileContainer}>
        <Box className={classes.fileInput}>
          <Button
            color="secondary"
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
          >
            {t('chooseFile')}
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_EXTS}
              onChange={handleFileChange}
            />
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="body1" className={classes.filenameText}>
            {file?.name ?? t('noFileSelected')}
          </Typography>
        </Box>
        {fileError && (
          <Typography variant="caption" color="primary" className={classes.errorContainer}>
            {fileError}
          </Typography>
        )}
      </Box>
      <Box>
        <Recaptcha
          ref={recaptchaRef}
          sitekey={Config.recaptchaSiteKey}
          onChange={handleCaptchaChange}
        />
        {captchaError && (
          <Typography variant="caption" color="primary" className={classes.errorContainer}>
            {captchaError}
          </Typography>
        )}
      </Box>
      <Box className={classes.buttonWrapper}>
        <Button fullWidth color="primary" variant="contained" type="submit" disabled={isSubmitting}>
          Upload
        </Button>
        {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
      </Box>
      <Button color="secondary" variant="outlined" type="reset">
        Reset
      </Button>
    </form>
  );
};

const TextField = (props: TextFieldProps) => {
  const classes = useStyles();
  return (
    <BaseTextField
      {...props}
      variant="outlined"
      FormHelperTextProps={{ className: classes.textFieldHelperText }}
    />
  );
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(2)}px`
  },
  fileContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  fileInput: {
    display: 'flex',
    alignItems: 'center',
    gap: `${theme.spacing(2)}px`
  },
  filenameText: {
    maxWidth: 400,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  errorContainer: {
    display: 'inline-block',
    marginTop: theme.spacing(1)
  },
  textFieldHelperText: {
    color: '#f00'
  },
  buttonWrapper: {
    width: '100%',
    position: 'relative'
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
}));

export default BulkImportUploadForm;
